/**
 * TrackDetails Component
 */

import * as playerActions from '../../../../redux/actions/player';
import { toggleTrackDetailsVisibility } from '../../../../redux/actions/ui';
import { licenseUsagesToSearchParams } from '../../../../redux/helpers/license';
import { getLocationPathname } from '../../../../redux/selectors/router';
import { isUserLoggedIn } from '../../../../redux/selectors/user';
import { getFormattedTime } from '../../../../utils/date';
import { capitalizeString } from '../../../../utils/string';
import {
  getArtistPagePath,
  getDefaultTrackAsset,
  getResponsiveTrackThumbnail,
  getTrackAssets,
  getTrackDuration,
  getTrackPagePath,
} from '../../../../utils/track';
import './styles.scss';

import { chain } from 'lodash';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import HotKeys from 'react-hot-keys';
import { connect } from 'react-redux';

import { getPlayerTracksFromResults } from '../../../../redux/helpers/player';
import { getFormattedMoney } from '../../../../utils/money';
import AddToCartButton from '../../../atoms/buttons/AddToCartButton';
import AddToProjectButton from '../../../atoms/buttons/AddToProjectButton';
import Button, { ButtonProps } from '../../../atoms/buttons/Button';
import DownloadButton from '../../../atoms/buttons/DownloadButton';
import TrackLicenseBuilderButton from '../../../atoms/buttons/LicenseBuilderButton/TrackLicenseBuilderButton';
import LinkButton from '../../../atoms/buttons/LinkButton';
import LinkButtonWithLicense from '../../../atoms/buttons/LinkButtonWithLicense';
import {
  IconAddToCart,
  IconDownload,
  IconInCart,
  IconMusicNote,
  IconPlayerPause,
  IconPlayerPlay,
  IconProject,
  IconProjectSelected,
  IconUser,
  IconWarning,
} from '../../../atoms/images/Icon';
import Thumbnail from '../../../atoms/images/Thumbnail';
import Heading from '../../../atoms/text/Heading';
import { Span } from '../../../atoms/text/Label';
import PriceTag from '../../../atoms/text/PriceTag';
import DropdownMenu from '../../../molecules/blocks/DropdownMenu';
import ThumbnailWithPlayButton from '../../../molecules/buttons/ThumbnailWithPlayButton';
import { MakeAnOfferTooltip } from '../../../molecules/messaging/MakeAnOfferTooltip';
import Drawer from '../../../molecules/modals/Drawer';
import Tag from '../../../molecules/Tag';
import { CartPagePath } from '../../../pages/Cart';
import * as Selector from './selectors';
import { SimilarTracks } from './SimilarTracks';
import {
  IOwnDispatchProps,
  IOwnProps,
  IOwnState,
  IOwnStateProps,
  TrackDetailsProps,
} from './types';

const drawerClasses = new BEM('drawer');
const classes = new BEM('track-details');
const defaultList = [];

class TrackDetails extends React.Component<TrackDetailsProps, IOwnState> {
  constructor(props: TrackDetailsProps) {
    super(props);

    this.state = {
      downloadMenuOpen: false,
      isOpen: true,
      hasSimilarTracks: true,
    };
  }

  componentWillReceiveProps(nextProps: TrackDetailsProps) {
    if (nextProps.trackInfo.id !== this.props.trackInfo.id) {
      this.setState({
        hasSimilarTracks: true,
      });
    }
  }

  onKeyDown = (_: string, { preventDefault }: KeyboardEvent) => {
    preventDefault();
  }

  onToggleDropdownMenu = (downloadMenuOpen: boolean) => {
    this.setState({ downloadMenuOpen });
  }

  onDownloadRequest = () => {
    this.setState({ isOpen: false });
  }

  renderHeader() {
    const {
      currency,
      currentVersionPlaying,
      hasTrackExclusions,
      trackCanBeLicensed,
      trackInfo,
      trackIsInCart,
    } = this.props;

    const title = trackInfo.title!;
    const artist = trackInfo.artist!.name!;
    const defaultVersion = getDefaultTrackAsset(trackInfo.assets!);
    const thumbnail = getResponsiveTrackThumbnail(trackInfo);
    const duration = getTrackDuration({ track: trackInfo, version: defaultVersion });
    const commerceButtonProps: ButtonProps = {
      colorScheme: 'accent',
      variant: 'pill',
      size: 'medium',
      withShadow: true,
      ...classes('button-add-to-cart'),
    };

    return (
      <div {...classes('header')}>
        {thumbnail.blur && (
          <Thumbnail
            {...classes('header__background')}
            src={thumbnail.blur![0].path}
            responsive
          />
        )}
        <ThumbnailWithPlayButton
          trackId={currentVersionPlaying || defaultVersion.id}
          responsiveAssets={thumbnail}
          size="large"
          withShadow
          {...classes('thumbnail')}
        />
        <Heading
          hyperLinkProps={{
            href: getTrackPagePath(trackInfo),
          }}
          is="h4"
          text={title}
          {...classes('title')}
        />
        <Span
          hyperLinkProps={{
            href: getArtistPagePath(trackInfo),
          }}
          text={artist}
          size="medium"
          textColor="bw7"
          {...classes('artist')}
        />
        {!!duration && (
          <Span
            text={getFormattedTime(duration)}
            size="small"
            textColor="bw6"
            {...classes('duration')}
          />
        )}
        {trackIsInCart ? (
          <LinkButton
            to={CartPagePath}
            label="Track is in your cart"
            icon={<IconInCart />}
            {...commerceButtonProps}
            withShadow={false}
          />
        ) : trackCanBeLicensed ? (
          <>
            <AddToCartButton
              label={<PriceTag amount={trackInfo.price!} label="License for" />}
              item={trackInfo}
              {...commerceButtonProps}
            />
            <MakeAnOfferTooltip
              buttonClassName="make-an-offer"
              artist={artist}
              trackTitle={title}
              trackPrice={getFormattedMoney(trackInfo.price!, currency)}
              withButton
            />
          </>
        ) : hasTrackExclusions ? (
          <Button
            label="This track cannot be licensed for the selected&nbsp;region."
            icon={<IconWarning />}
            style={{ mixBlendMode: 'multiply' }}
            disabled
            {...commerceButtonProps}
          />
        ) : (
          <TrackLicenseBuilderButton
            track={trackInfo.slug!}
            label="License This Track"
            icon={<IconAddToCart />}
            onSaveLicense={this.props.close}
            {...commerceButtonProps}
          />
        )}
      </div>
    );
  }

  renderActions() {
    const {
      currentPage,
      trackInfo,
    } = this.props;

    const downloadableAsset = getDefaultTrackAsset(trackInfo.assets!);
    const artistPagePath = getArtistPagePath(trackInfo);
    const trackPagePath = getTrackPagePath(trackInfo);
    const versions = getTrackAssets({ assets: trackInfo.assets || defaultList, format: 'mp3', type: 'track-preview' });

    return (
      <div {...classes('actions', '', 'drawer__button-list')}>

        {versions.length > 1 ? (
          <>
            <DropdownMenu
              align="justify"
              Button={(
                <Button
                  icon={this.props.trackIsInProjects ? <IconProjectSelected /> : <IconProject />}
                  label="Save"
                  {...drawerClasses('button-list__button')}
                />
              )}
              options={versions.map((asset) => (
                <AddToProjectButton
                  key={`save-${asset.version}`}
                  showIcon={false}
                  trackId={trackInfo.id}
                  trackVersion={asset.version!}
                  label={capitalizeString(asset.version!)}
                  {...drawerClasses('button-list__button')}
                />
              ))}
              requestClose={this.state.downloadMenuOpen === false}
              disableOnClickOutside={this.state.downloadMenuOpen === false}
              onToggle={this.onToggleDropdownMenu}
            />
            <DropdownMenu
              align="justify"
              Button={(
                <Button
                  icon={<IconDownload />}
                  label="Download Preview MP3"
                  {...drawerClasses('button-list__button')}
                />
              )}
              options={versions.map((asset) => (
                <DownloadButton
                  key={`download-${asset.version}`}
                  icon={<IconMusicNote />}
                  label={capitalizeString(asset.version!)}
                  assetPath={asset.path!}
                  onDownloadRequest={this.onDownloadRequest}
                  track={trackInfo}
                />
              ))}
              requestClose={this.state.downloadMenuOpen === false}
              disableOnClickOutside={this.state.downloadMenuOpen === false}
              onToggle={this.onToggleDropdownMenu}
            />
          </>
        ) : (
            <>
              <AddToProjectButton trackId={trackInfo.id} {...drawerClasses('button-list__button')} />
              <DownloadButton
                assetPath={downloadableAsset.path!}
                onDownloadRequest={this.onDownloadRequest}
                track={trackInfo}
                {...drawerClasses('button-list__button')}
              />
            </>
          )}
        {/*<Button label="Share" icon={<IconShare />} {...drawerClasses('button-list__button') } />*/}
        {trackPagePath !== currentPage &&
          <LinkButtonWithLicense
            to={trackPagePath}
            external
            label="Go to Track"
            icon={<IconMusicNote />}
            lineItemLicense={Selector.getCartLineItem(this.props)}
            {...drawerClasses('button-list__button')}
          />
        }
        {artistPagePath !== currentPage &&
          <LinkButtonWithLicense
            to={artistPagePath}
            external
            label="Go to Artist"
            icon={<IconUser />}
            lineItemLicense={Selector.getCartLineItem(this.props)}
            {...drawerClasses('button-list__button')}
          />
        }
        {/*<Button
          label="Listen on Spotify"
          icon={<IconSpotify />}
          {...drawerClasses('button-list__button', '', 'button--spotify') }
        />*/}
      </div>
    );
  }

  renderVersions() {
    const {
      currentVersionPlaying,
      pausePlayer,
      playTrackVersion,
      trackInfo,
    } = this.props;

    const versions = getTrackAssets({ assets: trackInfo.assets || defaultList, format: 'mp3', type: 'track-preview' });

    const buttons: JSX.Element[] = [];
    versions.forEach(({ id, version }, i) => {
      const PlayButton = currentVersionPlaying === id ? IconPlayerPause : IconPlayerPlay;
      buttons.push(
        <Button
          onClick={() => currentVersionPlaying === id ? pausePlayer() : playTrackVersion(id, trackInfo)}
          key={id}
          label={`${capitalizeString(version!)} Version`}
          icon={<PlayButton />}
          {...drawerClasses('button-list__button')}
        />,
      );
    });

    return versions.length > 1 && (
      <div {...classes('versions', '', 'drawer__button-list')} data-title="Versions">
        {buttons}
      </div>
    );
  }

  renderMatchedTags() {
    const {
      trackInfo,
    } = this.props;

    const terms =
      chain(trackInfo.terms!.edges)
        .map(({ node }) => node!)
        .filter(({ is_highlighted }) => !!is_highlighted)
        .value();

    return !!terms.length && (
      <div {...classes('matched-tags', '', 'drawer__content-group')} data-title="Matched Tags">
        {terms.map(({ id, name }) => <Tag key={id} id={id} label={name!} colorScheme="light" />)}
      </div>
    );
  }

  render() {
    const {
      className,
      displayInline,
      license,
      trackInfo,
    } = this.props;

    const hasAttributes = !!trackInfo.stems_available || !!trackInfo.customizable;
    const content = (
      <>
        {this.renderHeader()}
        {this.renderActions()}
        {this.renderVersions()}
        {this.renderMatchedTags()}
        {hasAttributes &&
          <div {...classes('attributes', '', 'drawer__content-group')} data-title="Attributes">
            {trackInfo.stems_available && <Tag id="stems-available" label="Stems available" />}
            {trackInfo.customizable && <Tag id="customizable" label="Customizable" />}
          </div>
        }
        <SimilarTracks
          artistSlug={trackInfo.artist!.slug!}
          licenseParams={licenseUsagesToSearchParams(license)}
          trackId={trackInfo.id}
          className="drawer__content-group"
        />
      </>
    );

    return (
      <HotKeys
        keyName="space"
        onKeyDown={this.onKeyDown}
      >
        {displayInline ? (
          <div {...classes('', 'inline', className)}>
            {content}
          </div>
        ) : (
          <Drawer
            closeButtonLabel="Close"
            floatingHeader
            headerIcon={null}
            onClose={this.props.close}
            open={this.state.isOpen}
            position="right"
            {...classes('', '', className)}
          >
            {content}
          </Drawer>
        )}
      </HotKeys>
    );
  }
}

const ConnectedTrackDetails = connect<IOwnStateProps, IOwnDispatchProps, IOwnProps, IStoreState>(
  (state, ownProps) => ({
    currentPage: getLocationPathname(state),
    currentVersionPlaying: Selector.currentVersionPlaying(state, ownProps),
    hasTrackExclusions: Selector.getTrackHasExclusions(state, ownProps),
    license: Selector.getLicense(state, ownProps),
    trackCanBeLicensed: Selector.getTrackCanBeLicensed(state, ownProps),
    trackInfo: Selector.getTrackInfo(state, ownProps),
    trackIsInCart: Selector.getTrackIsInCart(state, ownProps),
    trackIsInProjects: Selector.isTrackInProjects(state, ownProps),
    userIsLoggedIn: isUserLoggedIn(state),
  }),
  (dispatch, stateProps) => ({
    close: () => {
      dispatch(toggleTrackDetailsVisibility(false));
    },
    pausePlayer: () => {
      dispatch(playerActions.pause());
    },
    playTrackVersion: (trackId, trackInfo) => {
      if (trackInfo) {
        dispatch(playerActions.addTracksToQueue({ tracks: getPlayerTracksFromResults([trackInfo]) }));
      }
      dispatch(playerActions.playTrackFromQueue({ trackId }));
    },
  }),
)(TrackDetails);

export default ConnectedTrackDetails;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/organisms/modals/TrackDetails/index.tsx